import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { QRCodeSVG } from "qrcode.react";
import { Users, ExternalLink, Calendar, MapPin, Share2, ArrowLeft } from "lucide-react";

export default function EventDashboard() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();

  const event = useQuery(
    api.events.getBySlug,
    eventSlug ? { slug: eventSlug } : "skip"
  );

  const guests = useQuery(
    api.guests.listByEvent,
    event ? { eventId: event._id } : "skip"
  );

  if (event === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Memuat data event...
      </div>
    );
  }

  if (event === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 gap-2">
        <p className="font-bold text-slate-800">Event Tidak Ditemukan</p>
        <Button size="sm" onClick={() => navigate("/dashboard")}>Kembali ke Dashboard</Button>
      </div>
    );
  }

  const checkInUrl = `${window.location.origin}/checkin/${event.slug}`;

  const copyLink = () => {
    navigator.clipboard.writeText(checkInUrl);
    alert("Link check-in disalin!");
  };

  const shareWA = () => {
    const text = `Halo! Silakan check-in untuk acara *${event.name}* melalui link berikut:\n\n${checkInUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <header className="px-6 py-4 flex items-center justify-between border-b bg-white">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="font-bold text-lg text-slate-900">{event.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} className="text-xs">
            Salin Link
          </Button>
          <Button size="sm" onClick={shareWA} className="text-xs gap-1">
            <Share2 className="w-3.5 h-3.5" /> Bagikan WA
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Detail Acara */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Detail Acara</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-slate-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span>{new Date(event.date).toLocaleDateString("id-ID")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span>{event.location}</span>
              </div>
              {event.description && (
                <p className="pt-2 border-t text-slate-500">{event.description}</p>
              )}
            </CardContent>
          </Card>

          {/* QR Code Check-in */}
          <Card className="flex flex-col items-center justify-center p-6 text-center">
            <QRCodeSVG value={checkInUrl} size={130} />
            <span className="text-xs text-slate-400 mt-3">Scan untuk Check-in Tamu</span>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 text-xs"
              onClick={() => window.open(checkInUrl, "_blank")}
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Buka Webview
            </Button>
          </Card>

          {/* Statistik Tamu */}
          <Card className="flex flex-col justify-center p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <span className="text-3xl font-extrabold text-slate-900">
                  {guests?.length || 0}
                </span>
                <p className="text-xs text-slate-500 font-medium">Tamu Terdaftar</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabel Daftar Tamu */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daftar Tamu yang Telah Check-in</CardTitle>
            <CardDescription>Daftar terupdate otomatis secara real-time</CardDescription>
          </CardHeader>
          <CardContent>
            {guests === undefined ? (
              <p className="text-xs text-slate-400">Memuat tamu...</p>
            ) : guests.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-xs">
                Belum ada tamu yang check-in. Bagikan QR Code kepada para hadirin.
              </div>
            ) : (
              <div className="divide-y text-xs">
                {guests.map((g) => (
                  <div key={g._id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-slate-900">{g.name}</p>
                      <p className="text-slate-400 font-mono">{g.phone}</p>
                    </div>
                    <div className="text-right text-slate-400">
                      <span>{new Date(g.checkinTime).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}